import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import Calling from '../../api/Calling';
import Loader from '../../components/Loader';
import { FiX, FiClock, FiUsers, FiAward, FiCheckCircle, FiXCircle, FiUser, FiMail, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaArrowLeft, FaArrowRight, FaExternalLinkAlt, FaSortAmountDownAlt, FaSortAmountUp } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import { MdDeleteSweep } from 'react-icons/md';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import MainButton from '../../components/ul/MainButton';
import DeleteButton from '../../components/ul/DeleteButton';
import NotFound from '../../components/ul/NotFound';
import getISTDate from '../../components/getISTDate';

const ReferralPlanHistory = () => {
    const [loader, setLoader] = useState(true);
    const [historyData, setHistoryData] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectState, setSelectState] = useState('today');
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit] = useState(10);

    // Stats - calculated from current filtered data
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        expired: 0,
    });

    const fetchHistoryData = useCallback(async (page = 1) => {
        setLoader(true);
        try {
            const response = await Calling.getReferralPlanHistory(
                page,
                limit,
                startDate,
                endDate,
                statusFilter,
                sortOrder,
                search,
                ''
            );

            if (response?.status) {
                const data = response?.data || [];
                setHistoryData(data);
                setCurrentPage(response?.page || 1);
                setTotalPages(response?.totalPage || 1);
                setTotalRecords(response?.totalRecord || 0);

                // Calculate stats from current page data
                const activeCount = data.filter(item => item.status === 'active').length;
                const expiredCount = data.filter(item => item.status === 'expired').length;

                setStats({
                    total: response?.totalRecord || 0,
                    active: activeCount,
                    expired: expiredCount,
                });
            } else {
                toast.error(response?.message || 'Failed to fetch referral plan history');
                setHistoryData([]);
                setStats({ total: 0, active: 0, expired: 0 });
            }
        } catch (error) {
            toast.error('Failed to fetch referral plan history');
            setHistoryData([]);
            setStats({ total: 0, active: 0, expired: 0 });
        } finally {
            setLoader(false);
        }
    }, [limit, startDate, endDate, statusFilter, sortOrder, search]);

    useEffect(() => {
        fetchHistoryData(1);
    }, [startDate, endDate, statusFilter, sortOrder, search]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showModal]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showModal]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            case 'all':
                start = '';
                end = '';
                setSelectState('all');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1);
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
        } else {
            setEndDate(e.target.value);
        }
        setSelectState('range');
        setCurrentPage(1);
    };

    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchInput(value);

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearch(value);
            setCurrentPage(1);
        }, 500);
    };

    const handleReportSort = () => {
        setSelectState('sort');
        setSortOrder(sortOrder === 1 ? -1 : 1);
        setCurrentPage(1);
    };

    const handleStatusFilter = (status) => {
        if (statusFilter === status) {
            setStatusFilter('');
            setSelectState('today');
        } else {
            setStatusFilter(status);
            setSelectState(status);
        }
        setCurrentPage(1);
    };

    const handleSelectAll = () => {
        if (!selectAll) {
            const allIds = historyData.map((item) => item._id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectOne = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            return toast.error('Please select at least one record');
        }

        const check = generateCaptcha(`Delete ${selectedIds.length} record(s)? Solve:`);
        if (!check) return toast.error('Incorrect answer', { autoClose: 2000 });

        setLoader(true);
        try {
            const response = await Calling.deleteReferralPlanHistory({ ids: selectedIds });
            if (response?.status) {
                toast.success(response?.message || 'Records deleted successfully');
                setSelectedIds([]);
                setSelectAll(false);
                fetchHistoryData(currentPage);
            } else {
                toast.error(response?.message || 'Failed to delete');
                setLoader(false);
            }
        } catch (error) {
            toast.error('Failed to delete records');
            setLoader(false);
        }
    };

    const handleUserClick = (userId) => {
        if (userId) {
            window.open(`/manage/user/all/info/${userId}`, '_blank');
        }
    };

    const handleRowClick = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            setCurrentPage(page);
            setSelectedIds([]);
            setSelectAll(false);
            fetchHistoryData(page);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-th text-main-blue flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-blue'></span> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-yellow/10 text-main-yellow flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-yellow'></span> Expired</span>;
            default:
                return <span className='px-3 py-1 w-max text-xs font-medium rounded-full bg-main-bg text-main-subfont flex items-center gap-1'><span className='w-1.5 h-1.5 rounded-full bg-main-subfont'></span> {status}</span>;
        }
    };

    // User Avatar Component
    const UserAvatar = ({ profile, name }) => {
        const getInitials = (name) => {
            if (!name) return '?';
            return name.charAt(0).toUpperCase();
        };

        if (profile) {
            return (
                <img
                    src={profile}
                    alt={name}
                    className="w-10 h-10 rounded-xl object-cover border-2 border-main-border"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
            );
        }

        return (
            <div className="w-10 h-10 rounded-xl bg-main-blue flex items-center justify-center text-white font-semibold text-sm border-2 border-main-blue/30">
                {getInitials(name)}
            </div>
        );
    };

    return (
        <>
            <div className="w-full min-h-screen text-main-font">
                {/* Page Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                            <FiAward size={24} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-main-font">Referral Plan History</h1>
                            <p className="text-sm text-main-subfont">Track user referral plan activations and status</p>
                        </div>
                    </div>
                </div>

                {/* Main Card */}
                <div className='bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden'>
                    {/* Stats Cards Grid */}
                    <div className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <StatCard
                                title="Total Records"
                                value={stats.total}
                                icon={FiAward}
                                bgColor="bg-main-th"
                                iconBgColor="bg-main-exBlue/10"
                                textColor="text-main-exBlue"
                                borderColor="border-main-exBlue ring-main-exBlue/30"
                                loader={loader && currentPage === 1}
                                onClick={() => {
                                    setStatusFilter('');
                                    setSelectState('today');
                                    setCurrentPage(1);
                                }}
                                isActive={statusFilter === ''}
                            />
                            <StatCard
                                title="Active Plans"
                                value={stats.active}
                                icon={FiCheckCircle}
                                bgColor="bg-green-50"
                                iconBgColor="bg-green-100"
                                textColor="text-green-600"
                                borderColor="border-green-500 ring-green-300"
                                loader={loader && currentPage === 1}
                                onClick={() => handleStatusFilter('active')}
                                isActive={selectState === 'active'}
                            />
                            <StatCard
                                title="Expired Plans"
                                value={stats.expired}
                                icon={FiClock}
                                bgColor="bg-yellow-50"
                                iconBgColor="bg-yellow-100"
                                textColor="text-yellow-600"
                                borderColor="border-yellow-500 ring-yellow-300"
                                loader={loader && currentPage === 1}
                                onClick={() => handleStatusFilter('expired')}
                                isActive={selectState === 'expired'}
                            />
                        </div>
                    </div>

                    {/* Date Range Filters */}
                    <div className="px-4 py-3 border-t border-main-border">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex flex-wrap gap-2">
                                <MainButton
                                    onClick={() => handleDateRangeFilter('today')}
                                    label={'Today'}
                                    selectState={selectState === 'today'}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('yesterday')}
                                    label={'Yesterday'}
                                    selectState={selectState === "yesterday"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisWeek')}
                                    label={'This Week'}
                                    selectState={selectState === 'thisWeek'}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisMonth')}
                                    label={'This Month'}
                                    selectState={selectState === 'thisMonth'}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('last6Months')}
                                    label={'Last 6 Months'}
                                    selectState={selectState === 'last6Months'}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisYear')}
                                    label={'This Year'}
                                    selectState={selectState === 'thisYear'}
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="date"
                                    value={startDate}
                                    name="startDate"
                                    onChange={handleFormDate}
                                    placeholder="Start Date"
                                    className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                        ? 'border-main-blue text-main-blue bg-main-th'
                                        : 'border-main-border text-main-subfont bg-main-white'
                                        } focus:border-main-blue focus:outline-none`}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    name="endDate"
                                    onChange={handleFormDate}
                                    placeholder="End Date"
                                    className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                        ? 'border-main-blue text-main-blue bg-main-th'
                                        : 'border-main-border text-main-subfont bg-main-white'
                                        } focus:border-main-blue focus:outline-none`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons and Search */}
                    <div className="px-4 py-3 border-t border-main-border">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex flex-wrap gap-2">
                                <MainButton
                                    onClick={handleReportSort}
                                    label={
                                        <span className='flex gap-2 items-center'>
                                            {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                            Sort
                                        </span>
                                    }
                                    selectState={selectState === 'sort'}
                                />
                            </div>

                            <div className="relative w-full lg:w-80">
                                <IoMdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont" size={20} />
                                <input
                                    type="search"
                                    className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue focus:outline-none transition-all"
                                    value={searchInput}
                                    onChange={handleSearchInput}
                                    placeholder="Search by User or Plan..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className='w-full'>
                            <thead>
                                <tr className='bg-main-th'>
                                    <th className="ps-4 py-4 text-left">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                className='w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer'
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            />
                                            {selectedIds.length > 0 && (
                                                <DeleteButton
                                                    onClick={handleBulkDelete}
                                                    label={<MdDeleteSweep size={20} />}
                                                />
                                            )}
                                        </div>
                                    </th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Plan</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Duration</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Invites</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>

                            {loader ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={8} className="text-center py-12">
                                            <Loader />
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody className="divide-y divide-main-border">
                                    {historyData.length > 0 ? historyData.map((item, index) => (
                                        <tr key={item._id} className='hover:bg-main-thHover cursor-pointer transition-colors'>
                                            <td className="ps-4 py-4" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className='w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer'
                                                    checked={selectedIds.includes(item._id)}
                                                    onChange={() => handleSelectOne(item._id)}
                                                />
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(item)}>
                                                {(currentPage - 1) * limit + index + 1}
                                            </td>
                                            <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                                <div
                                                    className="flex items-center gap-3 cursor-pointer group"
                                                    onClick={() => handleUserClick(item?.u_info?._id)}
                                                >
                                                    <UserAvatar
                                                        profile={item?.u_info?.u_profile}
                                                        name={item?.u_info?.u_name}
                                                    />
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="text-main-blue font-medium text-sm group-hover:underline flex items-center gap-1 truncate max-w-[150px]">
                                                            {item?.u_info?.u_name || 'N/A'}
                                                            <FaExternalLinkAlt size={9} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </span>
                                                        {item?.u_info?.u_email ? (
                                                            <span className="text-xs text-main-subfont flex items-center gap-1 truncate max-w-[150px]">
                                                                <FiMail size={10} className="flex-shrink-0" />
                                                                {item?.u_info?.u_email}
                                                            </span>
                                                        ) : item?.u_info?.u_device ? (
                                                            <span className="text-xs text-main-subfont truncate max-w-[150px]">
                                                                Device: {item?.u_info?.u_device?.slice(0, 12)}...
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                                <span className="font-medium text-main-font">{item.r_plan_name || 'N/A'}</span>
                                            </td>
                                            <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-main-th text-main-blue rounded-lg text-sm font-medium border border-main-blue/20">
                                                    <FiClock size={12} />
                                                    {item.r_plan_day || 0} Days
                                                </span>
                                            </td>
                                            <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-main-th text-main-blue rounded-lg text-sm font-medium border border-main-blue/20">
                                                    <FiUsers size={12} />
                                                    {item.u_ref_count || 0} / {item.r_plan_invite_count || 0}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4" onClick={() => handleRowClick(item)}>
                                                {getStatusBadge(item.status)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(item)}>{formatDate(item.createdAt)}</td>
                                        </tr>
                                    )) : <tr><td colSpan={8} className="py-12"><NotFound /></td></tr>}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination */}
                    {!loader && historyData.length > 0 && (
                        <div className="p-6 border-t border-main-border">
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                <div className="text-sm text-main-subfont">
                                    Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} entries
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                            ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                            : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                            }`}
                                    >
                                        <FaArrowLeft size={12} /> Previous
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages
                                            ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                            : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                            }`}
                                    >
                                        Next <FaArrowRight size={12} />
                                    </button>
                                </div>

                                <p className="text-main-subfont text-sm">
                                    Page{' '}
                                    <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                        {currentPage}
                                    </span>
                                    {' '}of{' '}
                                    <span className="font-medium text-main-font">{totalPages}</span>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {showModal && selectedPlan && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div ref={modalRef} className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-main-border">
                        {/* Modal Header */}
                        <div className="bg-main-blue p-5 text-white relative">
                            <button
                                onClick={() => { setShowModal(false); setSelectedPlan(null); }}
                                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors"
                            >
                                <FiX size={16} />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-white/20 rounded-xl">
                                    <FiAward size={24} />
                                </div>
                                <div>
                                    <p className="text-white/80 text-sm">Referral Plan</p>
                                    <h2 className="text-xl font-bold">{selectedPlan?.r_plan_name || 'N/A'}</h2>
                                </div>
                            </div>
                            <div className="mt-3">{getStatusBadge(selectedPlan?.status)}</div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                            {/* User Card */}
                            <div
                                onClick={() => handleUserClick(selectedPlan?.u_info?._id)}
                                className="bg-main-th border border-main-blue/20 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {selectedPlan?.u_info?.u_profile ? (
                                            <img
                                                src={selectedPlan?.u_info?.u_profile}
                                                alt={selectedPlan?.u_info?.u_name}
                                                className="w-12 h-12 rounded-xl object-cover border-2 border-main-blue/20"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-xl bg-main-blue flex items-center justify-center text-white font-semibold text-lg border-2 border-main-blue/30">
                                                {selectedPlan?.u_info?.u_name?.charAt(0)?.toUpperCase() || '?'}
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-main-blue font-medium uppercase tracking-wide">User</p>
                                            <p className="font-semibold text-main-font group-hover:text-main-blue transition-colors">
                                                {selectedPlan?.u_info?.u_name || 'N/A'}
                                            </p>
                                            {selectedPlan?.u_info?.u_email ? (
                                                <p className="text-xs text-main-subfont mt-0.5 flex items-center gap-1">
                                                    <FiMail size={10} />
                                                    {selectedPlan?.u_info?.u_email}
                                                </p>
                                            ) : selectedPlan?.u_info?.u_device ? (
                                                <p className="text-xs text-main-subfont mt-0.5">
                                                    Device: {selectedPlan?.u_info?.u_device}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    <FaExternalLinkAlt className="text-main-blue group-hover:translate-x-1 transition-transform" size={12} />
                                </div>
                            </div>

                            {/* Plan Details Grid */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Duration</p>
                                    <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.r_plan_day || 0}</p>
                                    <p className="text-xs text-main-subfont">Days</p>
                                </div>
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Invites</p>
                                    <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.r_plan_invite_count || 0}</p>
                                    <p className="text-xs text-main-subfont">Allowed</p>
                                </div>
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Referred</p>
                                    <p className="text-xl font-bold text-main-blue mt-1">{selectedPlan?.u_ref_count || 0}</p>
                                    <p className="text-xs text-main-subfont">Users</p>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="bg-main-bg border border-main-border rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <FiClock className="text-main-subfont" size={14} />
                                    <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">Timeline</p>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">Start</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.start_date)}</p>
                                    </div>
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">End</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.end_date)}</p>
                                    </div>
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">Created</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.createdAt)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 bg-main-bg border-t border-main-border">
                            <button
                                onClick={() => handleUserClick(selectedPlan?.u_info?._id)}
                                className="w-full px-4 py-2.5 bg-main-blue text-white rounded-xl hover:bg-main-blue/90 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                                <FiUser size={14} /> View User Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReferralPlanHistory;

// Sub-components
const StatCard = ({ title, value, icon: Icon, bgColor, iconBgColor, textColor, borderColor, loader, onClick, isActive }) => (
    <div
        onClick={onClick}
        className={`${bgColor} rounded-xl p-4 border transition-all cursor-pointer hover:shadow-md ${isActive
            ? `${borderColor} ring-2 ring-offset-1`
            : 'border-main-border hover:border-main-blue/30'
            }`}
    >
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-main-subfont">{title}</p>
                <p className={`text-2xl font-bold mt-1 ${textColor}`}>
                    {loader ? (
                        <span className="flex items-center text-main-subfont text-base">
                            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            ...
                        </span>
                    ) : (
                        value.toLocaleString()
                    )}
                </p>
            </div>
            <div className={`p-3 rounded-xl ${iconBgColor}`}>
                <Icon size={24} className={textColor} />
            </div>
        </div>
    </div>
);